import { Typography } from '@/components/atoms/Typography';
import { useI18nContext, useUIColors } from '@/core/hooks';
import { isSmallScreen } from '@/core/utils/responsive';
import React, { useEffect } from 'react';
import {
  Animated,
  Modal,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/** Above dialog / loader overlays (typically zIndex 1000). */
const TOAST_LAYER_Z_INDEX = 10001;

export type ToastPosition = 'top-center' | 'top-right';

export interface ToastProps {
  visible: boolean;
  message: string;
  duration?: number;
  onHide?: () => void;
  /**
   * Placement override. Default `top-center` keeps existing callers unchanged.
   * `top-right` is applied on web only; native always uses top-center.
   * On small web screens, top-right keeps the same top offset/size but is
   * horizontally centered with equal left/right insets.
   */
  position?: ToastPosition;
}

export const Toast: React.FC<ToastProps> = ({
  visible,
  message,
  duration = 2000,
  onHide,
  position = 'top-center',
}) => {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const insets = useSafeAreaInsets();
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const slideAnim = React.useRef(new Animated.Value(-100)).current;
  const isTopRight = Platform.OS === 'web' && position === 'top-right';
  const isTopRightMobile = isTopRight && isSmallScreen();

  useEffect(() => {
    if (!visible) {
      hideToast();
      return undefined;
    }

    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 50,
        friction: 7,
      }),
    ]).start();

    const timer = setTimeout(() => {
      hideToast();
    }, duration);

    return () => clearTimeout(timer);
  }, [visible, duration]);

  const hideToast = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: -100,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onHide?.();
    });
  };

  if (!visible) {
    return null;
  }

  const toastBody = (
    <View
      style={[
        styles.container,
        resolveToastPlacement(isTopRight, isTopRightMobile),
        webRtlContainerStyle(isTopRight, isRTL),
        resolveToastOffset(isTopRight, insets.top),
      ]}
      pointerEvents="none"
    >
      <Animated.View
        style={[
          styles.toast,
          isTopRight && styles.toastTopRight,
          isTopRightMobile && styles.toastTopRightMobile,
          {
            backgroundColor: colors.backgroundLevel1,
            ...(isTopRight ? { borderColor: colors.outlineInactive } : null),
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <Typography
          variant="body"
          color="primary"
          style={[
            styles.message,
            isTopRight && styles.messageTopRight,
            webRtlMessageStyle(isTopRight, isRTL),
            Platform.OS === 'web' ? styles.messageWebWrap : null,
          ]}
        >
          {message}
        </Typography>
      </Animated.View>
    </View>
  );

  return (
    <Modal
      visible
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={hideToast}
    >
      <View
        style={[
          styles.modalHost,
          Platform.OS === 'web' ? ({ zIndex: TOAST_LAYER_Z_INDEX } as ViewStyle) : null,
        ]}
        pointerEvents="box-none"
      >
        {toastBody}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalHost: {
    flex: 1,
  },
  container: {
    position: 'absolute',
    zIndex: TOAST_LAYER_Z_INDEX,
  },
  containerTopCenter: {
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  /** Anchored to viewport top-right (near header profile), not page content. */
  containerTopRight: {
    right: 32,
    left: 'auto',
    alignItems: 'flex-end',
    zIndex: TOAST_LAYER_Z_INDEX,
  },
  /** Same top offset as desktop top-right; equal left/right insets on small web. */
  containerTopRightMobile: {
    left: 16,
    right: 16,
    alignItems: 'stretch',
    zIndex: TOAST_LAYER_Z_INDEX,
  },
  containerTopRightOffset: {
    top: 24,
  },
  toast: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    minWidth: 200,
    maxWidth: '80%',
  },
  toastTopRight: {
    borderWidth: 1,
    maxWidth: 420,
    alignSelf: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  toastTopRightMobile: {
    width: '100%',
    maxWidth: '100%',
  },
  message: {
    textAlign: 'center',
  },
  messageTopRight: {
    textAlign: 'left',
  },
  messageWebWrap: Platform.OS === 'web'
    ? ({
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
      } as TextStyle)
    : {},
});

function resolveToastPlacement(
  isTopRight: boolean,
  isTopRightMobile: boolean,
): StyleProp<ViewStyle> {
  if (isTopRightMobile) {
    return styles.containerTopRightMobile;
  }
  if (isTopRight) {
    return styles.containerTopRight;
  }
  return styles.containerTopCenter;
}

function resolveToastOffset(
  isTopRight: boolean,
  insetTop: number,
): StyleProp<ViewStyle> {
  if (!isTopRight) {
    return { top: insetTop + 16 };
  }
  return [
    styles.containerTopRightOffset,
    Platform.OS === 'web' ? ({ position: 'fixed' } as const) : null,
  ];
}

function webRtlContainerStyle(
  isTopRight: boolean,
  isRTL: boolean,
): ViewStyle | null {
  if (!isTopRight || Platform.OS !== 'web' || !isRTL) {
    return null;
  }
  return { left: 32, right: 'auto', alignItems: 'flex-start' };
}

function webRtlMessageStyle(
  isTopRight: boolean,
  isRTL: boolean,
): TextStyle | null {
  if (!isTopRight || Platform.OS !== 'web' || !isRTL) {
    return null;
  }
  return { textAlign: 'right' };
}

export default Toast;
